<template>
  <transition name="fade">
    <div
      v-if="isBannerShown"
      class="cookie-banner"
    >
      <button
        role="button"
        class="cookie-banner__close"
        @click="dismiss"
      >
        <IconClose class="cookie-banner__close-icon" />
      </button>

      <div class="cookie-banner__content">
        <div class="cookie-banner__title">
          {{ $t('cookies.title') }}
        </div>
        <p
          v-sanitized-html="$t('cookies.description', { privacyLink })"
          class="cookie-banner__description"
        />
      </div>

      <div class="cookie-banner__actions">
        <BaseButton
          type="light-primary"
          @click="rejectAll"
        >
          {{ $t('cookies.reject') }}
        </BaseButton>
        <BaseButton @click="consentAll">
          {{ $t('cookies.consent_all') }}
        </BaseButton>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, inject, computed } from 'vue'
import BaseButton from '@/components/button/BaseButton.vue'
import IconClose from '@/components/icons/IconClose.vue'
import { useI18n } from 'vue-i18n'
import { loadHotjar } from '@/utils/hotjar.js'

const { t } = useI18n()
const consent = inject('consent')
const isBannerShown = ref(false)

const privacyUrl = 'https://rocken.jobs/datenschutz-agb'

function close() {
  isBannerShown.value = false
}

function consentAll() {
  const { ANALYTICS, MARKETING } = consent.cookies
  consent?.save([ANALYTICS, MARKETING], true)
  loadHotjar()

  close()
}

function rejectAll() {
  consent?.save([], false)

  close()
}

// Closing the banner without an active choice must not be treated as consent (CJEU C-673/17, Planet49).
function dismiss() {
  rejectAll()
}

const privacyLink = computed(() => {
  return `<a href="${privacyUrl}" target="_blank">${t('cookies.privacy_link_text')}</a>`
})

onMounted(() => {
  const cookieConsent = consent?.get()

  if (cookieConsent === null || cookieConsent.isConsented === null) {
    isBannerShown.value = true
  }
})

</script>

<style lang="scss">
.cookie-banner {
  position: fixed;
  bottom: var(--space-32);
  left: var(--space-32);
  right: var(--space-32);
  background-color: var(--color-white);
  border: 1px solid var(--color-zinc-200);
  border-radius: var(--border-radius-4);
  padding: var(--space-32);
  z-index: 1002;
  box-shadow: var(--shadow-hight);
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-20);

  &__title {
    font-size: var(--subtitle-1);
    line-height: var(--leading-160);
    font-weight: var(--weight-semibold);
    margin-bottom: var(--space-16);
  }

  &__description {
    color: var(--color-zinc-400);
    line-height: 1.7;
    margin: 0;

    a {
      color: var(--color-brand-500);
    }
  }

  &__actions {
    display: flex;
    gap: var(--space-16);
    align-items: end;

    & > button.btn {
      padding-top: var(--space-12);
    }
  }

  &__close {
    position: absolute;
    padding: 0;
    border: 0;
    width: 24px;
    height: 24px;
    cursor: pointer;
    right: var(--space-20);
    top: var(--space-20);
    z-index: 1;
    border-radius: var(--space-40);
    background-color: var(--color-zinc-400);
    color: var(--color-white);
    display: grid;
    place-items: center;
  }

  &__close-icon {
    width: 12px;
    height: 12px;
  }

  @include tablet-up {
    grid-template-columns: 64% 1fr;
  }
}

.fade-enter-active {
  transition: opacity 1s;
}

.fade-enter-from {
  opacity: 0;
}
</style>
